import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  Switch,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { ArrowClockwiseRegular } from '@fluentui/react-icons';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { Chip } from '../components/Chip.js';
import { useIsEcosoftStaff } from '../stores/authStore.js';
import { getSesionesActivas, type SesionesActivas, type SesionActiva } from '../api/staffPresencia.js';

/**
 * Quién está usando OPUS ai ahora — SOLO staff Ecosoft.
 *
 * Existe para responder una sola pregunta, la que hay que contestar antes de
 * actualizar producción: **¿le voy a cortar la sesión a alguien?** Hasta ahora eso
 * se averiguaba entrando al servidor a leer registros a mano, así que dependía de
 * que alguien supiera hacerlo.
 *
 * La pantalla distingue a propósito dos cosas que no son lo mismo:
 *
 *  · **Trabajando** — hizo algo en los últimos 5 minutos. Hay una persona ahí.
 *  · **Pestaña abierta** — hay una ventana del navegador conectada, que puede estar
 *    olvidada desde hace horas. Por eso al lado va CUÁNDO fue su última acción: sin
 *    ese dato, una pestaña dormida se ve igual que alguien trabajando (nos pasó el
 *    19/08 con la cuenta de una tester: tres horas de conexión viva y nadie enfrente).
 */

const REFRESCO_MS = 15_000;

const useStyles = makeStyles({
  page: { minHeight: '100vh', background: 'var(--bg-canvas)' },
  body: { padding: '20px 28px 64px', maxWidth: '1100px', margin: '0 auto' },
  head: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '18px' },
  small: { fontSize: tokens.fontSizeBase200, color: 'var(--text-secondary)' },
  barra: {
    display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap',
    padding: '12px 14px', marginBottom: '14px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
  },
  derecha: { marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' },
  veredicto: {
    display: 'flex', flexDirection: 'column', gap: '2px',
    padding: '16px 18px', marginBottom: '14px',
    borderRadius: tokens.borderRadiusMedium,
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-surface)',
  },
  veredictoOk: { borderLeft: '4px solid var(--ok, #2ea043)' },
  veredictoOcupado: { borderLeft: '4px solid var(--warn, #d29922)' },
  titulo: { fontSize: tokens.fontSizeBase500, fontWeight: 600, color: 'var(--text-primary)' },
  tablaWrap: {
    overflowX: 'auto',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
  },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: tokens.fontSizeBase200 },
  th: {
    textAlign: 'left', padding: '10px 12px', whiteSpace: 'nowrap',
    color: 'var(--text-secondary)', fontWeight: 600,
    borderBottom: '1px solid var(--border-subtle)',
  },
  td: {
    padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)',
    color: 'var(--text-primary)', verticalAlign: 'top',
  },
  num: { fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' },
  ruta: { fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-secondary)' },
  vacio: { padding: '28px 16px', textAlign: 'center', color: 'var(--text-secondary)' },
});

/** "hace 3 min" / "ahora mismo" / "sin acciones". Nada de fechas crudas en la tabla. */
function textoDeActividad(s: SesionActiva): string {
  if (s.haceMinutos == null) return 'sin acciones';
  if (s.haceMinutos === 0) return 'ahora mismo';
  if (s.haceMinutos === 1) return 'hace 1 minuto';
  if (s.haceMinutos < 60) return `hace ${s.haceMinutos} minutos`;
  const h = Math.floor(s.haceMinutos / 60);
  return h === 1 ? 'hace 1 hora' : `hace ${h} horas`;
}

/** Verde = persona trabajando. Ámbar = pestaña abierta sin actividad reciente. */
function colorDe(s: SesionActiva): string {
  if (s.haceMinutos != null && s.haceMinutos <= 5) return 'var(--ok, #2ea043)';
  if (s.canalesAbiertos > 0) return 'var(--warn, #d29922)';
  return 'var(--text-secondary)';
}

function horaLocal(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

export function StaffSesionesActivasPage() {
  const styles = useStyles();
  const isStaff = useIsEcosoftStaff();

  const [datos, setDatos] = useState<SesionesActivas | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresco, setAutoRefresco] = useState(true);
  const [ultimaLectura, setUltimaLectura] = useState<Date | null>(null);

  const cargar = useCallback(async () => {
    try {
      const r = await getSesionesActivas(30);
      setDatos(r);
      setUltimaLectura(new Date());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo leer las sesiones activas');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { void cargar(); }, [cargar]);

  useEffect(() => {
    if (!autoRefresco) return;
    const t = window.setInterval(() => void cargar(), REFRESCO_MS);
    return () => window.clearInterval(t);
  }, [autoRefresco, cargar]);

  if (!isStaff) return <Navigate to="/" replace />;

  const trabajando = datos?.trabajando ?? 0;
  const libre = datos != null && trabajando === 0 && datos.conPestanaAbierta === 0;

  return (
    <div className={styles.page}>
      <AppTopBar />
      <div className={styles.body}>
        <Breadcrumbs items={[{ label: 'Inicio', to: '/' }, { label: 'Sesiones activas (staff)' }]} />
        <div className={styles.head}>
          <Text size={600} weight="semibold">Sesiones activas</Text>
          <Text className={styles.small}>
            Quién está usando OPUS ai u OPUS ci en este momento. Se mira antes de actualizar el servidor:
            una actualización reinicia el sistema y le corta la sesión a quien esté trabajando.
          </Text>
        </div>

        {error && (
          <MessageBar intent="error" style={{ marginBottom: '14px' }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.barra}>
          <Chip label="Trabajando" count={trabajando} dotColor="var(--ok, #2ea043)"
                title="Hizo algo en los últimos 5 minutos" />
          <Chip label="Pestaña abierta" count={datos?.conPestanaAbierta ?? 0}
                dotColor="var(--warn, #d29922)"
                title="Tiene una ventana conectada; puede estar olvidada" />
          {/* Tercer chip: sin el, los dos de arriba daban 0 y la tabla mostraba 3 filas,
              sin nada que explicara de donde salian. La tabla usa una ventana de 30 min
              y el veredicto una de 5: son preguntas distintas y ahora las dos estan a la
              vista. Reportado por Sebastian el 2026-08-21 mirando el panel. */}
          <Chip label="Activos hace poco" count={datos?.total ?? 0}
                dotColor="var(--text-tertiary, #8b949e)"
                title="Hicieron algo en los ultimos 30 minutos: son los que lista la tabla" />
          <div className={styles.derecha}>
            {ultimaLectura && (
              <Text className={styles.small}>Leído a las {horaLocal(ultimaLectura.toISOString())}</Text>
            )}
            <Switch checked={autoRefresco} onChange={(_, d) => setAutoRefresco(!!d.checked)}
                    label="Actualizar solo" />
            <Button appearance="subtle" icon={<ArrowClockwiseRegular />}
                    onClick={() => void cargar()} disabled={cargando}>
              Actualizar
            </Button>
          </div>
        </div>

        {cargando && !datos ? (
          <Spinner label="Leyendo…" />
        ) : (
          <>
            <div className={`${styles.veredicto} ${libre ? styles.veredictoOk : styles.veredictoOcupado}`}>
              <span className={styles.titulo}>
                {libre
                  ? 'Nadie está trabajando ahora: se puede actualizar'
                  : trabajando > 0
                    ? `Hay ${trabajando} ${trabajando === 1 ? 'persona trabajando' : 'personas trabajando'}: mejor esperar`
                    : 'Nadie está trabajando, pero hay ventanas abiertas'}
              </span>
              <Text className={styles.small}>
                {libre
                  ? (datos && datos.total > 0
                      ? `Nadie hizo nada en los últimos 5 minutos ni tiene una ventana conectada. ${datos.total === 1 ? 'Una persona estuvo activa' : `${datos.total} personas estuvieron activas`} en la última media hora.`
                      : 'Nadie hizo nada en los últimos 5 minutos ni tiene una ventana conectada.')
                  : trabajando > 0
                    ? 'Actualizar ahora les corta lo que estén haciendo.'
                    : 'Actualizar sólo va a cerrarles la ventana; mira la última acción de cada uno para decidir.'}
              </Text>
            </div>

            <div className={styles.tablaWrap}>
              <table className={styles.tabla}>
                <thead>
                  <tr>
                    <th className={styles.th}>Quién</th>
                    <th className={styles.th}>Empresa</th>
                    <th className={styles.th}>Última acción</th>
                    <th className={styles.th}>Qué estaba haciendo</th>
                    <th className={styles.th}>Ventanas</th>
                  </tr>
                </thead>
                <tbody>
                  {(datos?.sesiones ?? []).map((s) => (
                    <tr key={s.userId}>
                      <td className={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            width: '8px', height: '8px', borderRadius: '50%',
                            background: colorDe(s), flexShrink: 0,
                          }} />
                          <div>
                            <div>{s.nombre}</div>
                            {s.email && <div className={styles.small}>{s.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td className={styles.td}>{s.empresa}</td>
                      <td className={`${styles.td} ${styles.num}`}>{textoDeActividad(s)}</td>
                      <td className={styles.td}>
                        <span className={styles.ruta}>{s.ultimaRuta ?? '—'}</span>
                      </td>
                      <td className={`${styles.td} ${styles.num}`}>{s.canalesAbiertos || '—'}</td>
                    </tr>
                  ))}
                  {(datos?.sesiones.length ?? 0) === 0 && (
                    <tr>
                      <td className={styles.vacio} colSpan={5}>
                        Nadie conectado en los últimos {datos?.ventanaMinutos ?? 30} minutos.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {datos && (
              <Text className={styles.small} style={{ display: 'block', marginTop: '10px' }}>
                El conteo arranca de cero cada vez que se actualiza el servidor; este lleva
                andando desde las {horaLocal(datos.motorDesde)}. Alguien que entró antes y no
                hizo nada desde entonces puede no aparecer.
              </Text>
            )}
          </>
        )}
      </div>
    </div>
  );
}
